import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trophy, Hexagon, Share2, Volume2, VolumeX } from "lucide-react";
import { sfxKick, sfxGoal, sfxLoss, sfxJackpot, sfxCommentator, setArcadeMuted, isArcadeMuted } from "@/lib/arcadeSound";
import { shareArcadeCard } from "@/lib/shareCard";
import { tr } from "@/lib/tr";
import { burstCooldownLeft, startBurstCooldown, cooldownToast, BurstCooldownChip, BurstProgress } from "./AutoRun";
import { useServerBurst, ServerBurstPanel } from "./ServerBurst";
import { getRecovTarget } from "@/lib/recovery";
import Confetti from "@/components/arcade/Confetti";
import MarketScanner from "@/components/arcade/MarketScanner";
import { CameraFlashes, StreakFlame } from "@/components/arcade/CasinoFX";

const IMG = {
  ready: "/arcade/striker_ready.png",
  kick: "/arcade/striker_kick.png",
  keeper: "/arcade/keeper_ready.png",
  dive: "/arcade/keeper_dive.png",
  ball: "/arcade/ball.png",
};

const TAUNTS = {
  start: [
    "Right here. Try me.",
    "Pick a corner — I already know it.",
    "I've seen your type before. All boots, no brains.",
    "This net is closed for business.",
    "My grandma shoots harder than you look.",
    "I saved a penalty in my sleep last night. Warm-up done.",
    "Go on. Make my highlight reel.",
    "The goal is 7 meters wide and you still won't find it.",
    "I already texted my mum I'm keeping a clean sheet.",
    "Even/Odd? Doesn't matter. I'm built different.",
    "Take your time. Fear looks good on you.",
  ],
  goal: [
    "Lucky bounce. Go again.",
    "Wind took that one. Rematch.",
    "Enjoy it — that's your last one.",
    "My glove slipped. Won't happen twice.",
    "The sun was in my eyes. At night. Indoors. Whatever.",
    "I let you have that one. Confidence tax.",
    "My shoelace was untied. Ask VAR.",
    "That corner was wet. Groundskeeper's fault.",
    "I blinked. First time since 2019.",
    "Cool goal. Anyway, I've reset the simulation.",
    "My horoscope said to be generous today.",
    "That was practice. THIS one counts.",
  ],
  save: [
    "Read you like a book. A short one. With pictures.",
    "Is that all you've got?",
    "I could do this all day.",
    "Saw it before you even kicked. Saw it last week actually.",
    "DENIED. Tell your friends.",
    "I'm putting that one on my fridge.",
    "You shoot like you trade without a stop-loss.",
    "That ball is mine now. Adoption papers are ready.",
    "GPS said left. You still went wrong.",
    "My gloves are still yawning.",
    "Somewhere, your coach just sighed.",
    "Next time aim for the goal. It's the big rectangle.",
  ],
  nervous: [
    "Okay okay okay… you're warm. Doesn't mean anything.",
    "I'm not sweating, it's raining. Inside. On my face only.",
    "Streak means nothing. I'm due a save. Statistically. Please.",
    "My agent said not to talk to you anymore.",
    "Alright hotshot. I've called my brother. He's bigger.",
    "You again?? Does this guy not have a job?",
    "I'm just letting you build false confidence. Part of the plan.",
    "One more and I'm switching to a smaller goal.",
  ],
};
const pickTaunt = (k) => TAUNTS[k][Math.floor(Math.random() * TAUNTS[k].length)];

const COMEBACKS = [
  "Top bins. Check the net for holes.",
  "GPS said you'd dive wrong. It was right.",
  "Tell VAR to review that scream.",
  "I aim where your excuses can't reach.",
  "That's three points and your dignity.",
  "Keeper? I thought you were a mannequin.",
  "Even my ball felt bad for you.",
  "Bank it. Frame it. Next.",
  "You dove yesterday. The ball went today.",
  "Say hi to the net for me — you two are close now.",
];
const pickComeback = () => COMEBACKS[Math.floor(Math.random() * COMEBACKS.length)];

/** Penalty Shootout — LEFT = EVEN, RIGHT = ODD. A real Deriv Even/Odd
 * contract decides the outcome. Photoreal striker + keeper sprites, ball
 * flight, GOAL popups with confetti + crowd sound, 7-streak jackpot. */
const PenaltyShootout = ({ runFlip, runContract, busy, report, celebrate, digits, initial, onPersist, baseStake = 0.35, logExternal }) => {
  const [phase, setPhase] = useState("idle"); // idle | pending | goal | saved
  const [pose, setPose] = useState("ready");  // ready | kick
  const [dir, setDir] = useState(null);       // L | R
  const [keeper, setKeeper] = useState(null); // L | R
  const [popup, setPopup] = useState(null);   // {kind, profit}
  const [score, setScore] = useState(() => ({ goals: initial?.goals || 0, saves: initial?.saves || 0 })); // 💾 restored from server
  const [streak, setStreak] = useState(() => initial?.streak || 0);
  const [best, setBest] = useState(() => Number(localStorage.getItem("vts_goal_best_streak") || 0));
  const [badge7, setBadge7] = useState(() => localStorage.getItem("vts_goal_badge_7") === "1");
  const [jackpot, setJackpot] = useState(false);
  const [taunt, setTaunt] = useState(() => pickTaunt("start"));
  const [comeback, setComeback] = useState(null);
  const [lastKick, setLastKick] = useState(null); // 🔎 plain-language result of the last kick
  const [deficit, setDeficit] = useState(0);      // 🔁 losses since last goal (for the recovery chip)
  const [armedStake, setArmedStake] = useState(null); // one-tap recovery stake for the NEXT kick
  const [muted, setMuted] = useState(isArcadeMuted());
  const [autoN, setAutoN] = useState(0);        // ⚡ auto-kick burst size (0 = off)
  const [autoProg, setAutoProg] = useState(null);
  // ⚽/📈 kick mode — digits (Even/Odd) or Rise/Fall (left = FALL, right = RISE)
  const [penMode, setPenMode] = useState(() => { try { return localStorage.getItem("vts_pen_mode") === "risefall" ? "risefall" : "digits"; } catch { return "digits"; } });
  const modeRef = useRef(penMode);
  useEffect(() => { modeRef.current = penMode; try { localStorage.setItem("vts_pen_mode", penMode); } catch { /* noop */ } }, [penMode]);
  const autoStopRef = useRef(false);
  const streakWon = useRef(0);
  const timers = useRef([]);
  const pitchRef = useRef(null); // 🎥 auto-scroll target so the kick is always visible

  useEffect(() => {
    Object.values(IMG).forEach((src) => { const i = new Image(); i.src = src; });
    return () => timers.current.forEach(clearTimeout);
  }, []);

  // 💾 push score changes up so they persist across page reloads
  useEffect(() => {
    onPersist?.({ goals: score.goals, saves: score.saves, streak });
  }, [score, streak]); // eslint-disable-line react-hooks/exhaustive-deps
  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));

  const share = async (title, value, sub) => {
    const r = await shareArcadeCard({ title, value, sub });
    if (r === "downloaded") toast.success("Card saved to your device — share it anywhere");
  };

  // 📡 per-game Even/Odd Signal Engine — kicks fire on the locked market
  const [mkt, setMkt] = useState(null);
  const symRef = useRef("1HZ100V");
  const mktRef = useRef(null);
  const onScan = useCallback((row) => { symRef.current = row.sym; mktRef.current = row; setMkt(row); }, []);

  // 🖥 ×50/×100 seasons fire on the VTS server — they keep kicking after the
  // app closes. While you're WATCHING, every server kick replays live on the
  // pitch and lands in the session history.
  const replayServerKick = useCallback((fresh, stB) => {
    fresh.forEach((r) => logExternal?.({
      label: "Penalty Shootout", won: r.won, profit: r.profit, stake: r.stake,
      digit: r.digit, side: stB.side, sym: stB.symbol,
    }));
    const r = fresh[fresh.length - 1];
    const fw = fresh.filter((x) => x.won).length;
    const fl = fresh.length - fw;
    const d = (stB.side === "EVEN" || stB.side === "FALL") ? "L" : "R";
    setDir(d); setKeeper(null); setPopup(null); setComeback(null);
    setPhase("pending");
    later(() => { setPose("kick"); sfxKick(); }, 200);
    later(() => {
      const kd = r.won ? (d === "L" ? "R" : "L") : d;
      setKeeper(kd);
      setLastKick({ pick: stB.side, digit: r.digit, won: r.won, profit: r.profit });
      setPhase(r.won ? "goal" : "saved");
      setStreak((s) => { let ns = s; fresh.forEach((x) => { ns = x.won ? ns + 1 : 0; }); return ns; });
      setScore((s) => ({ goals: s.goals + fw, saves: s.saves + fl }));
      if (r.won) { sfxGoal(1); setPopup({ kind: "goal", profit: r.profit }); }
      else { sfxLoss(); setPopup({ kind: "saved", profit: r.profit }); }
      later(() => { setPhase("idle"); setPose("ready"); setDir(null); setKeeper(null); setPopup(null); }, 1500);
    }, 850);
  }, [logExternal]); // eslint-disable-line react-hooks/exhaustive-deps
  const srv = useServerBurst("penalty", replayServerKick);
  const fire = (d, follow = false) => {
    if (autoN >= 50) {
      const rf2 = modeRef.current === "risefall";
      srv.start({ kind: rf2 ? "risefall" : "evenodd",
        side: rf2 ? (d === "L" ? "FALL" : "RISE") : (d === "L" ? "EVEN" : "ODD"),
        n: autoN, stake: Math.max(0.35, Number(baseStake) || 0.35), symbol: symRef.current, duration: 1 });
      return;
    }
    if (autoN > 0) autoRun(d, autoN, follow); else shoot(d);
  };

  const shoot = async (d, override = null) => {
    if (busy || phase === "pending") return null;
    try { pitchRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (e) { /* ignore */ }
    setDir(d);
    setKeeper(null);
    setPopup(null);
    setComeback(null);
    setPhase("pending");
    later(() => { setPose("kick"); sfxKick(); }, 250);
    const armed = override ?? armedStake;
    setArmedStake(null); // one kick per selection
    const rf = modeRef.current === "risefall";
    const res = rf
      ? await runContract({ contractType: d === "L" ? "PUT" : "CALL", customStake: armed ?? baseStake, label: "Penalty Shootout", symbol: symRef.current })
      : await runFlip(d === "L" ? "EVEN" : "ODD", armed, "Penalty Shootout", symRef.current);
    if (!res) { setPhase("idle"); setPose("ready"); setDir(null); return null; }
    const kd = res.won ? (d === "L" ? "R" : "L") : d;
    setKeeper(kd);
    setLastKick({ pick: rf ? (d === "L" ? "FALL" : "RISE") : (d === "L" ? "EVEN" : "ODD"), digit: res.digit, won: res.won, profit: res.profit });
    setPhase(res.won ? "goal" : "saved");
    if (res.won) {
      setScore((s) => ({ ...s, goals: s.goals + 1 }));
      setDeficit(0); // goal clears the recovery hole
      const ns = streak + 1;
      setStreak(ns);
      streakWon.current = Math.round((streakWon.current + res.profit) * 100) / 100;
      report?.("goal_streak", ns);
      if (ns > best) { setBest(ns); localStorage.setItem("vts_goal_best_streak", String(ns)); }
      later(() => {
        sfxGoal(1 + Math.min(ns, 7) * 0.18);
        sfxCommentator();
        setPopup({ kind: "goal", profit: res.profit });
        toast.success(`GOAL! Back of the net — +$${res.profit.toFixed(2)} settled`, { duration: 3500 });
      }, 480);
      if (ns > 0 && ns % 7 === 0) {
        later(() => {
          setJackpot(true);
          sfxJackpot();
          celebrate?.("goal7", streakWon.current);
          if (!badge7) { setBadge7(true); localStorage.setItem("vts_goal_badge_7", "1"); }
          later(() => setJackpot(false), 5200);
        }, 1400);
      }
    } else {
      setScore((s) => ({ ...s, saves: s.saves + 1 }));
      setDeficit((v) => Math.round((v + (res.stake || 0)) * 100) / 100);
      setStreak(0);
      streakWon.current = 0;
      later(() => {
        sfxLoss();
        setPopup({ kind: "saved", profit: res.profit });
        toast(`SAVED — keeper read it. −$${Math.abs(res.profit).toFixed(2)}`, { duration: 3000 });
      }, 480);
    }
    later(() => {
      setPhase("idle"); setPose("ready"); setDir(null); setKeeper(null); setPopup(null);
      setTaunt(pickTaunt(res.won ? (streak + 1 >= 3 ? "nervous" : "goal") : "save"));
      if (res.won) {
        setComeback(pickComeback());
        later(() => setComeback(null), 4500);
      }
    }, 2200);
    return res;
  };

  /* ⚡ AUTO-KICK: fires N real kicks back-to-back on one side. Doesn't change
     the ~50/50 odds — just removes the waiting. 🛡 Capital protection: a save
     auto-raises ONLY the next kick to the +20% recovery stake; the moment a
     goal recovers the hole the stake drops straight back to base. Safety
     brake: stops itself after 7 saves in a row; STOP button anytime. */
  const autoRun = async (d, n, follow = false) => {
    if (burstCooldownLeft("penalty") > 0) { cooldownToast("penalty"); return; }
    // 🧮 recovery brake honors the Smart Stake depth plan (depth 6/8 ladders
    // need up to ~77×/~393× base) — without a plan the default stays 10× base
    let planMult = 10;
    let planDepth = 0;
    try {
      const v = parseInt(localStorage.getItem("vts_arcade_recov_maxmult"), 10);
      if (v >= 10 && v <= 500) planMult = v;
      const dpt = parseInt(localStorage.getItem("vts_arcade_recov_cap"), 10);
      if (dpt >= 3 && dpt <= 6) planDepth = dpt;
    } catch { /* default */ }
    autoStopRef.current = false;
    let saves = 0;
    let hole = 0; // 🆕 every burst starts FRESH at base — no carrying old losses
    let w = 0, losses = 0, net = 0;
    let side = d;
    let completed = 0;
    let pausedLadder = false;
    for (let i = 1; i <= n; i++) {
      if (autoStopRef.current) break;
      // 🎯 follow mode: every kick re-reads the freshest best signal
      if (follow) {
        const s = mktRef.current?.signal;
        if (s && !s.wait) side = s.side === "EVEN" ? "L" : "R";
      }
      // ⛔ HARD BRAKE: recovery may NEVER stake more than the PLANNED multiple
      // of base (10× default; a Smart Stake depth 6/8 plan raises it so the
      // ladder can actually finish — one win anywhere recovers all +20%).
      let rec = null;
      if (hole > 0.009) {
        const ratio = modeRef.current === "risefall" ? 0.9 : 0.95;
        const tgt = 1 + getRecovTarget();
        const need = Math.max(0.35, Math.ceil(((hole * tgt) / ratio) * 100) / 100);
        const base = Math.max(0.35, Number(baseStake) || 0.35);
        // allowed = the planned ladder's own final kick (+2% rounding headroom)
        let simHole = base, maxNeed = base;
        for (let k = 0; k < (planDepth || 4); k++) {
          const s = Math.max(0.35, Math.ceil(((simHole * tgt) / ratio) * 100) / 100);
          maxNeed = s;
          simHole = Math.round((simHole + s) * 100) / 100;
        }
        const allowed = Math.max(maxNeed * 1.02, base * planMult);
        if (need > allowed) {
          // 🛑 the ladder can't finish within plan — PAUSE, never continue silently
          toast.error("Bot paused after a full losing streak. Review before restarting.", {
            description: "Auto-kick stopped itself — it will NOT restart on its own.", duration: 8000 });
          pausedLadder = true;
          break;
        }
        rec = need;
      }
      setAutoProg({ i, n, rec, w, l: losses, net });
      const res = await shoot(side, rec);
      if (!res) {
        if (completed === 0) toast.error(tr("burst_abort_title"), { description: tr("burst_abort_desc"), duration: 5000 });
        break;
      }
      completed += 1;
      net = Math.round((net + (res.profit || 0)) * 100) / 100;
      if (res.won) {
        if (rec) toast.success("💰 Hole recovered — stake drops back to BASE for the next kick", { duration: 2600 });
        hole = 0;
        saves = 0;
        w += 1;
      } else {
        hole = Math.round((hole + (res.stake || 0)) * 100) / 100;
        saves += 1;
        losses += 1;
      }
      // 🛑 full planned ladder lost (depth + final kick) — pause, never restart alone
      setAutoProg({ i, n, rec: null, w, l: losses, net, done: completed });
      if (saves >= ((planDepth || 4) + 1)) {
        toast.error("Bot paused after a full losing streak. Review before restarting.", {
          description: "Auto-kick stopped itself — it will NOT restart on its own.", duration: 8000 });
        pausedLadder = true;
        break;
      }
      if (i < n) await new Promise((r) => setTimeout(r, 900)); // faster pacing (Nov 2026) — settle time still rules
    }
    if (n >= 50 && completed >= n) {
      // 🧊 break ONLY after a full completed season — an aborted run never locks anything
      startBurstCooldown("penalty", 60);
      toast.success(tr("x30_done_title", { w, l: losses, net }), { description: tr("x30_done_desc"), duration: 6000 });
    } else if (completed > 0 && !pausedLadder) {
      // 🏁 every burst ends with its final score
      toast.success(`🏁 burst done — ${w}W / ${losses}L · net ${net >= 0 ? "+" : "−"}$${Math.abs(net).toFixed(2)}`, { duration: 4000 });
    }
    setAutoProg(null);
  };

  const resolved = phase === "goal" || phase === "saved";

  return (
    <div data-testid="penalty-game" className="space-y-4">
      {/* 📖 HOW A KICK WORKS — plain-language, always visible */}
      <div data-testid="penalty-howto" className="rounded-lg border border-white/10 bg-[#0B0E14] px-3 py-2">
        <p className="text-[10px] font-mono text-white/70 leading-relaxed">
          <span className="font-black text-white">HOW A KICK WORKS:</span>{" "}
          1️⃣ Pick <span className="text-[#F23645] font-bold">EVEN</span> or <span className="text-[#089981] font-bold">ODD</span>
          {" "}→ 2️⃣ a real Deriv contract fires on the next market tick → 3️⃣ the tick's <b>last digit</b> decides:
          your pick lands = <span className="text-[#089981] font-bold">⚽ GOAL (~×1.95 payout)</span>, misses = <span className="text-[#F23645] font-bold">🧤 keeper SAVES it (stake lost)</span>.
        </p>
        <p className="text-[9px] font-mono text-white/40 mt-0.5">
          Every kick is ~50/50 — past digits never predict the next one. Score 7 goals in a row for the Sharpshooter jackpot (badge + your real streak winnings, no bonus cash).
        </p>
      </div>
      <style>{`
        @keyframes pen-confetti {0%{transform:translateY(0) rotate(0)}100%{transform:translateY(320px) rotate(var(--spin,540deg));opacity:.15}}
        @keyframes pen-run {0%{transform:translateX(26px)}70%{transform:translateX(2px)}100%{transform:translateX(0)}}
        @keyframes pen-rise-L {0%{transform:translate(0,0) scale(1)}100%{transform:translate(-22px,-96px) scale(.72)}}
        @keyframes pen-rise-R {0%{transform:translate(0,0) scale(1)}100%{transform:translate(22px,-96px) scale(.72)}}
        @keyframes pen-land-L {0%{transform:translate(-22px,-96px) scale(.72)}100%{transform:translate(-104px,-158px) scale(.45)}}
        @keyframes pen-land-R {0%{transform:translate(22px,-96px) scale(.72)}100%{transform:translate(104px,-158px) scale(.45)}}
        @keyframes pen-save-L {0%{transform:translate(-22px,-96px) scale(.72)}70%{transform:translate(-96px,-190px) scale(.5)}100%{transform:translate(-88px,-168px) scale(.5)}}
        @keyframes pen-save-R {0%{transform:translate(22px,-96px) scale(.72)}70%{transform:translate(96px,-190px) scale(.5)}100%{transform:translate(88px,-168px) scale(.5)}}
        @keyframes pen-dive-save-L {0%{transform:translate(0,0)}100%{transform:translate(-36px,28px)}}
        @keyframes pen-dive-save-R {0%{transform:translate(0,0)}100%{transform:translate(36px,28px)}}
        @keyframes pen-spin {0%{transform:rotate(0)}100%{transform:rotate(360deg)}}
        @keyframes pen-dive-L {0%{transform:translate(0,0)}100%{transform:translate(-92px,-14px)}}
        @keyframes pen-dive-R {0%{transform:translate(0,0)}100%{transform:translate(92px,-14px)}}
        @keyframes pen-crowd-bob {0%{background-position:0 0,4px 3px}100%{background-position:0 2px,4px 1px}}
        @keyframes pen-crowd-flash {0%,100%{opacity:0}50%{opacity:.9}}
        @keyframes pen-sweep-a {0%,100%{transform:rotate(-16deg)}50%{transform:rotate(14deg)}}
        @keyframes pen-sweep-b {0%,100%{transform:rotate(15deg)}50%{transform:rotate(-13deg)}}
        @keyframes pen-net {0%,100%{transform:translateX(0)}25%{transform:translateX(-4px)}75%{transform:translateX(4px)}}
        @keyframes pen-flash {0%{opacity:.35}100%{opacity:0}}
        @keyframes pen-pop {0%{transform:scale(.7);opacity:0}60%{transform:scale(1.08);opacity:1}100%{transform:scale(1)}}
        @media (prefers-reduced-motion: reduce){ .pen-motion *{animation-duration:.01s !important} }
      `}</style>

      {/* stadium */}
      <div ref={pitchRef} className="pen-motion relative h-96 rounded-xl overflow-hidden border border-white/10"
        style={{ background: "linear-gradient(180deg,#070a10 0%,#0B0E14 40%,#0d1524 100%)" }}>
        {/* data-grid pitch floor */}
        <div className="absolute inset-x-0 bottom-0 h-[55%]" style={{
          backgroundImage: "linear-gradient(rgba(41,98,255,.16) 1px,transparent 1px),linear-gradient(90deg,rgba(41,98,255,.12) 1px,transparent 1px)",
          backgroundSize: "48px 28px",
          transform: "perspective(560px) rotateX(52deg)",
          transformOrigin: "top",
          maskImage: "linear-gradient(180deg,transparent,black 25%)",
        }} />
        {/* stadium lights glow */}
        <div className="absolute inset-x-0 top-0 h-28" style={{ background: "radial-gradient(ellipse at 50% -40%,rgba(41,98,255,.28),transparent 70%)" }} />

        {/* animated crowd behind the goal */}
        <div className="absolute inset-x-0 top-0" style={{
          height: "13%",
          backgroundImage: "radial-gradient(circle, rgba(180,200,255,.4) 1.1px, transparent 1.7px), radial-gradient(circle, rgba(247,183,49,.28) 1px, transparent 1.6px)",
          backgroundSize: "9px 7px, 13px 9px",
          animation: "pen-crowd-bob .7s ease-in-out infinite alternate",
          maskImage: "linear-gradient(180deg, black 60%, transparent)",
          opacity: Math.min(0.75 + streak * 0.05, 1),
        }} />
        {/* camera flashes in the crowd (goal + kick moments) */}
        {(phase === "goal" || phase === "pending") && (
          <div className="absolute inset-x-0 top-0 pointer-events-none" style={{
            height: "12%",
            backgroundImage: "radial-gradient(circle, rgba(255,255,255,.95) 1.6px, transparent 2.6px)",
            backgroundSize: phase === "goal" ? (streak >= 3 ? "22px 11px" : "34px 16px") : "90px 20px",
            backgroundPosition: "7px 3px",
            animation: `pen-crowd-flash ${phase === "goal" ? ".22s" : ".9s"} steps(2) infinite`,
          }} />
        )}
        {/* stadium light sweeps */}
        <div className="absolute pointer-events-none" style={{
          top: "-12%", left: "6%", width: "26%", height: "85%",
          background: "linear-gradient(180deg, rgba(140,175,255,.26), rgba(140,175,255,.08) 55%, transparent)",
          clipPath: "polygon(42% 0, 58% 0, 100% 100%, 0 100%)",
          transformOrigin: "top center",
          animation: "pen-sweep-a 7s ease-in-out infinite",
          mixBlendMode: "screen",
        }} />
        <div className="absolute pointer-events-none" style={{
          top: "-12%", right: "6%", width: "26%", height: "85%",
          background: "linear-gradient(180deg, rgba(247,183,49,.2), rgba(247,183,49,.07) 55%, transparent)",
          clipPath: "polygon(42% 0, 58% 0, 100% 100%, 0 100%)",
          transformOrigin: "top center",
          animation: "pen-sweep-b 8.5s ease-in-out infinite",
          mixBlendMode: "screen",
        }} />

        {/* goal frame + net */}
        <div className="absolute left-1/2 -translate-x-1/2" style={{
          top: "9%", width: "56%", height: "37%",
          borderTop: "4px solid rgba(255,255,255,.8)", borderLeft: "4px solid rgba(255,255,255,.8)", borderRight: "4px solid rgba(255,255,255,.8)",
          borderRadius: "6px 6px 0 0",
          boxShadow: "0 0 14px rgba(255,255,255,.18)",
          backgroundImage: "linear-gradient(rgba(255,255,255,.09) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.09) 1px,transparent 1px)",
          backgroundSize: "16px 14px",
          animation: phase === "goal" ? "pen-net .4s ease .45s 3" : "none",
        }} />
        {/* goal line */}
        <div className="absolute left-1/2 -translate-x-1/2 h-px bg-white/20" style={{ top: "46%", width: "74%" }} />

        {/* stadium jumbotron — live market digits */}
        {digits?.length > 0 && (
          <div data-testid="penalty-jumbotron" className="absolute left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 px-2 py-1 rounded-md border border-white/10 bg-black/75"
            style={{ top: "1.5%", boxShadow: "0 0 14px rgba(41,98,255,.15)" }}>
            <span className="text-[7px] font-mono uppercase tracking-[0.2em] text-[#089981] mr-0.5 animate-pulse">live</span>
            {digits.slice(-9).map((d, i, arr) => (
              <span key={i} className={`w-4 h-5 rounded-sm flex items-center justify-center text-[10px] font-black font-mono ${d % 2 === 0
                ? "text-[#ff8a95] bg-[#F23645]/15" : "text-[#0be9a8] bg-[#089981]/15"} ${i === arr.length - 1 ? "ring-1 ring-[#F7B731]/60" : ""}`}>
                {d}
              </span>
            ))}
          </div>
        )}

        {/* keeper trash talk between shots */}
        {phase === "idle" && taunt && (
          <div data-testid="penalty-keeper-taunt" className="absolute left-1/2 -translate-x-1/2 z-10" style={{ top: "12%" }}>
            <div className="relative px-3 py-1.5 rounded-lg border border-white/15 bg-black/80 text-[10px] font-mono text-white/85 max-w-[75vw] sm:max-w-[340px] text-center leading-snug"
              style={{ animation: "pen-pop .35s cubic-bezier(.3,1.2,.4,1)" }}>
              {taunt}
              <span className="absolute left-1/2 -bottom-1 -translate-x-1/2 w-2 h-2 rotate-45 bg-black/80 border-r border-b border-white/15" />
            </div>
          </div>
        )}

        {/* striker fires back after a goal — beef goes both ways */}
        {phase === "idle" && comeback && (
          <div data-testid="penalty-striker-comeback" className="absolute z-10" style={{ left: "58%", bottom: "46%" }}>
            <div className="relative px-3 py-1.5 rounded-lg border border-[#2962FF]/40 bg-[#0d1730]/90 text-[10px] font-mono text-[#9db8ff] max-w-[70vw] sm:max-w-[300px] text-center leading-snug"
              style={{ animation: "pen-pop .35s cubic-bezier(.3,1.2,.4,1) .25s both" }}>
              {comeback}
              <span className="absolute left-6 -bottom-1 w-2 h-2 rotate-45 bg-[#0d1730]/90 border-r border-b border-[#2962FF]/40" />
            </div>
          </div>
        )}

        {/* keeper */}
        <div className="absolute left-1/2 -translate-x-1/2" style={{ top: "17%" }}>
          <div style={{ animation: keeper ? `pen-dive-${phase === "saved" ? "save-" : ""}${keeper} .55s cubic-bezier(.3,.8,.4,1) forwards` : "none" }}>
            {keeper ? (
              <img src={IMG.dive} alt="keeper diving" className="h-16 w-auto max-w-none"
                style={{ transform: keeper === "L" ? "scaleX(-1)" : "none", filter: "drop-shadow(0 4px 10px rgba(0,0,0,.6))" }} />
            ) : (
              <img src={IMG.keeper} alt="goalkeeper" className="h-24 w-auto"
                style={{ filter: "drop-shadow(0 4px 10px rgba(0,0,0,.6))" }} />
            )}
          </div>
        </div>

        {/* striker */}
        <div className="absolute" style={{ left: "50%", marginLeft: "8px", bottom: "5%" }}>
          <div style={{ animation: phase !== "idle" ? "pen-run .55s ease-out forwards" : "none", transform: "translateX(26px)" }}>
            <img src={pose === "kick" ? IMG.kick : IMG.ready} alt="striker"
              className={pose === "kick" ? "h-36 w-auto max-w-none -scale-x-100" : "h-36 w-auto -scale-x-100"}
              style={{ filter: "drop-shadow(0 6px 12px rgba(0,0,0,.65))" }} />
          </div>
        </div>

        {/* ball */}
        <div className="absolute left-1/2 -translate-x-1/2" style={{ bottom: "9%" }}>
          <div style={{
            animation: resolved && dir
              ? `pen-${phase === "saved" ? "save" : "land"}-${dir} .5s cubic-bezier(.2,.8,.4,1) forwards`
              : phase === "pending" && dir
                ? `pen-rise-${dir} .9s cubic-bezier(.3,.7,.4,1) .5s forwards`
                : "none",
          }}>
            <img src={IMG.ball} alt="ball" className="w-9 h-9"
              style={{
                filter: "drop-shadow(0 0 10px rgba(247,183,49,.55))",
                animation: phase === "pending" || resolved ? "pen-spin .6s linear infinite" : "none",
              }} />
          </div>
        </div>

        {/* pending status */}
        {phase === "pending" && (
          <p data-testid="penalty-pending" className="absolute inset-x-0 bottom-2 text-center text-[10px] font-mono uppercase tracking-[0.25em] text-white/50 animate-pulse">
            ball in flight — contract settling
          </p>
        )}

        {/* red flash on save */}
        {phase === "saved" && <div className="absolute inset-0 bg-[#F23645]" style={{ animation: "pen-flash .8s ease .5s forwards", opacity: 0 }} />}

        {/* 📸 crowd goes wild — camera flashes on a goal */}
        {phase === "goal" && <CameraFlashes />}

        {/* 🔥 hot-streak chip — keep the run alive */}
        {streak >= 2 && (
          <div className="absolute left-2 bottom-2 z-10"><StreakFlame n={streak} /></div>
        )}

        {/* GOAL / SAVED popup */}
        {popup && !jackpot && (
          <div data-testid={popup.kind === "goal" ? "penalty-goal-popup" : "penalty-saved-popup"}
            className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-md">
            {popup.kind === "goal" && <Confetti />}
            <div className="relative text-center px-8 py-6 rounded-xl border border-white/20 bg-black/40" style={{ animation: "pen-pop .45s cubic-bezier(.3,1.2,.4,1)" }}>
              {popup.kind === "goal" ? (
                <>
                  <p className="text-5xl font-black tracking-tight text-[#089981]" style={{ textShadow: "0 0 24px rgba(8,153,129,.6)" }}>GOOOAL!</p>
                  <p className="mt-1 text-xl font-black font-mono text-[#F7B731]">+${popup.profit.toFixed(2)}</p>
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50 mt-1">settled to your balance</p>
                </>
              ) : (
                <>
                  <p className="text-5xl font-black tracking-tight text-[#F23645]" style={{ textShadow: "0 0 24px rgba(242,54,69,.5)" }}>SAVED</p>
                  <p className="mt-1 text-lg font-black font-mono text-white/80">−${Math.abs(popup.profit).toFixed(2)}</p>
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50 mt-1">keeper read it — next strike</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* 7-streak jackpot */}
        {jackpot && (
          <div data-testid="penalty-jackpot-overlay" className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-xl">
            <Confetti count={70} />
            <div className="relative text-center" style={{ animation: "pen-pop .5s cubic-bezier(.3,1.2,.4,1)" }}>
              <div className="mx-auto w-16 h-16 relative mb-2">
                <Hexagon className="w-16 h-16 text-[#F7B731]" style={{ filter: "drop-shadow(0 0 14px rgba(247,183,49,.8))" }} />
                <Trophy className="w-7 h-7 text-[#F7B731] absolute inset-0 m-auto" />
              </div>
              <p className="text-4xl font-black tracking-tight text-[#F7B731]" style={{ textShadow: "0 0 30px rgba(247,183,49,.7)" }}>7-STREAK JACKPOT</p>
              <p className="text-xs font-mono text-white/70 mt-1">Seven back-to-back goals — Sharpshooter badge unlocked</p>
              <p className="text-[9px] font-mono uppercase tracking-[0.2em] text-white/40 mt-1">achievement + your real streak winnings · no bonus cash</p>
              <button data-testid="penalty-share-jackpot-btn"
                onClick={() => share("7-STREAK JACKPOT", 7, "Penalty Shootout · seven back-to-back goals")}
                className="mt-3 inline-flex items-center gap-1.5 px-4 h-9 rounded-lg bg-[#F7B731] hover:bg-[#e0a41f] text-black text-[11px] font-black uppercase tracking-wide transition-colors">
                <Share2 className="w-3.5 h-3.5" /> Share this
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ⚡ signal shortcut — right under the pitch */}
      {penMode === "digits" && mkt?.signal && !mkt.signal.wait && (
        <button data-testid="penalty-signal-shoot" disabled={busy || phase === "pending" || !!autoProg || srv.running}
          onClick={() => { const d = mkt.signal.side === "EVEN" ? "L" : "R"; fire(d, true); }}
          className="w-full h-12 rounded-lg bg-[#F7B731] hover:brightness-110 text-[#0B0E14] font-black text-sm uppercase tracking-wide transition disabled:opacity-40 active:scale-[0.99]">
          ⚡ Shoot the signal — {mkt.signal.side === "EVEN" ? "LEFT · EVEN" : "RIGHT · ODD"} on {mkt.label || mkt.sym} · {mkt.signal.strength}/100{autoN > 0 ? ` · ×${autoN} burst · follows the best signal` : ""}
        </button>
      )}

      {/* ⚽/📈 kick-mode toggle — digits (Even/Odd) or Rise/Fall */}
      <div data-testid="penalty-mode-toggle" className="grid grid-cols-2 gap-2">
        <button data-testid="penalty-mode-digits" onClick={() => setPenMode("digits")}
          className={`h-10 rounded-lg border font-mono font-black text-[11px] transition-colors ${penMode === "digits" ? "border-[#F7B731] bg-[#F7B731]/15 text-[#F7B731]" : "border-white/15 text-white/45 hover:border-white/35"}`}>
          ⚽ DIGITS · EVEN / ODD
        </button>
        <button data-testid="penalty-mode-risefall" onClick={() => setPenMode("risefall")}
          className={`h-10 rounded-lg border font-mono font-black text-[11px] transition-colors ${penMode === "risefall" ? "border-[#53b9ff] bg-[#53b9ff]/15 text-[#53b9ff]" : "border-white/15 text-white/45 hover:border-white/35"}`}>
          📈 RISE / FALL · left = FALL, right = RISE
        </button>
      </div>
      {/* shoot controls — directly below the pitch so you always see the kick */}
      <div className="grid grid-cols-2 gap-3">
        <button data-testid="penalty-shoot-left" disabled={busy || phase === "pending" || !!autoProg || srv.running} onClick={() => fire("L")}
          className="h-16 rounded-lg bg-[#F23645] hover:bg-[#C92A37] text-white transition-colors disabled:opacity-40 active:scale-[0.98] flex flex-col items-center justify-center leading-tight"
          style={{ boxShadow: "0 0 18px rgba(242,54,69,.25)" }}>
          {penMode === "risefall" ? (
            <>
              <span className="font-black text-sm uppercase tracking-wide">⬇ Shoot Left · FALL</span>
              <span className="text-[10px] font-mono text-white/80">wins if the next tick closes LOWER</span>
            </>
          ) : (
            <>
              <span className="font-black text-sm uppercase tracking-wide">⚽ Shoot Left · EVEN{mkt && mkt.evenPct >= 56 ? " 🔥" : ""}</span>
              <span className="text-[10px] font-mono text-white/80">digits 0 · 2 · 4 · 6 · 8{mkt ? ` · recent E ${mkt.evenPct}%` : ""}</span>
            </>
          )}
        </button>
        <button data-testid="penalty-shoot-right" disabled={busy || phase === "pending" || !!autoProg || srv.running} onClick={() => fire("R")}
          className="h-16 rounded-lg bg-[#089981] hover:bg-[#067a67] text-white transition-colors disabled:opacity-40 active:scale-[0.98] flex flex-col items-center justify-center leading-tight"
          style={{ boxShadow: "0 0 18px rgba(8,153,129,.25)" }}>
          {penMode === "risefall" ? (
            <>
              <span className="font-black text-sm uppercase tracking-wide">⬆ Shoot Right · RISE</span>
              <span className="text-[10px] font-mono text-white/80">wins if the next tick closes HIGHER</span>
            </>
          ) : (
            <>
              <span className="font-black text-sm uppercase tracking-wide">⚽ Shoot Right · ODD{mkt && mkt.evenPct <= 44 ? " 🔥" : ""}</span>
              <span className="text-[10px] font-mono text-white/80">digits 1 · 3 · 5 · 7 · 9{mkt ? ` · recent O ${100 - mkt.evenPct}%` : ""}</span>
            </>
          )}
        </button>
      </div>

      {/* 🔎 plain-language result of the last kick */}
      {lastKick && (
        <div data-testid="penalty-last-kick" className={`rounded-lg border px-3 py-2 text-center text-[11px] font-mono ${lastKick.won ? "border-[#089981]/40 bg-[#089981]/10 text-[#089981]" : "border-[#F23645]/40 bg-[#F23645]/10 text-[#F23645]"}`}>
          You shot <b>{lastKick.pick}</b>{lastKick.digit != null && (
            <> · the tick's last digit came <b>{lastKick.digit}</b> ({lastKick.digit % 2 === 0 ? "EVEN" : "ODD"})</>
          )} → {lastKick.won ? <b>⚽ GOAL +${lastKick.profit.toFixed(2)}</b> : <b>🧤 SAVED −${Math.abs(lastKick.profit).toFixed(2)}</b>}
        </div>
      )}

      {/* 🔁 one-tap recovery stake (appears after a loss) — honest math shown */}
      {deficit > 0.009 && (() => {
        const tgtP = getRecovTarget();
        const ratio = penMode === "risefall" ? 0.9 : 0.95;
        const rec = Math.max(0.35, Math.ceil(((deficit * (1 + tgtP)) / ratio) * 100) / 100);
        const base = Math.max(0.35, Number(baseStake) || 0.35);
        // ⛔ same ladder-based allowance as auto-kick — a dead ladder can't be chased
        let simHole = base, maxNeed = base;
        for (let k = 0; k < 4; k++) {
          const s = Math.max(0.35, Math.ceil(((simHole * (1 + tgtP)) / ratio) * 100) / 100);
          maxNeed = s;
          simHole = Math.round((simHole + s) * 100) / 100;
        }
        const allowed = Math.max(maxNeed * 1.02, base * 10);
        if (rec > allowed) {
          return (
            <div data-testid="penalty-recovery-braked" className="rounded-lg border border-[#F23645]/40 bg-[#F23645]/10 px-3 py-2">
              <p className="text-[10px] font-mono text-white/75">
                Down <b className="text-[#F23645]">${deficit.toFixed(2)}</b> this run — winning it back in one kick would need <b>${rec.toFixed(2)}</b>, beyond your planned ladder.
                <span className="text-white/40 block">⛔ Recovery brake: that's too deep to chase. This loss is accepted — next kicks fire at base stake. Protecting your balance beats one desperate kick.</span>
              </p>
            </div>
          );
        }
        const tgtLbl = Math.round(tgtP * 100);
        return (
          <div data-testid="penalty-recovery-chip" className="rounded-lg border border-[#F7B731]/40 bg-[#F7B731]/10 px-3 py-2 flex items-center gap-2 flex-wrap">
            <p className="text-[10px] font-mono text-white/75 flex-1 min-w-[180px]">
              Down <b className="text-[#F23645]">${deficit.toFixed(2)}</b> this run. A <b className="text-[#F7B731]">${rec.toFixed(2)}</b> kick that scores targets it all back{tgtLbl > 0 ? <b> + ~{tgtLbl}%</b> : null}.
              <span className="text-white/40 block">⚠️ Still ~50/50 — a miss makes the hole ${(deficit + rec).toFixed(2)}. Never chase more than you can afford.</span>
            </p>
            <button data-testid="penalty-recovery-use" onClick={() => setArmedStake(rec)}
              className={`px-3 py-1.5 rounded-md text-[10px] font-black font-mono transition ${armedStake === rec ? "bg-[#F7B731] text-black" : "border border-[#F7B731]/60 text-[#F7B731] hover:bg-[#F7B731]/20"}`}>
              {armedStake === rec ? `✓ ARMED $${rec.toFixed(2)}` : `USE $${rec.toFixed(2)} NEXT KICK`}
            </button>
            {armedStake != null && (
              <button data-testid="penalty-recovery-clear" onClick={() => setArmedStake(null)}
                className="px-2 py-1.5 rounded-md border border-white/20 text-[10px] font-mono text-white/60 hover:text-white transition">
                back to base
              </button>
            )}
          </div>
        );
      })()}

      {/* ⚡ auto-kick burst selector */}
      <div data-testid="penalty-auto-row" className="flex items-center gap-2 flex-wrap">
        <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-white/40">⚡ Auto-kick</span>
        {[0, 3, 5, 10, 20, 50, 100].map((n) => (
          <button key={n} data-testid={`penalty-auto-${n}`} disabled={!!autoProg}
            onClick={() => { if (n > 1 && burstCooldownLeft("penalty") > 0) { cooldownToast("penalty"); return; } setAutoN(n); }}
            className={`px-2.5 py-1 rounded-md text-[10px] font-black font-mono transition ${autoN === n ? "bg-[#2962FF] text-white" : "border border-white/15 text-white/50 hover:text-white"}`}>
            {n === 0 ? "OFF" : `×${n}`}
          </button>
        ))}
        <BurstCooldownChip id="penalty" />
        {autoN > 0 && !autoProg && (
          <span className="text-[9px] font-mono text-white/40">{autoN >= 50
            ? `🖥 tap a side once → ×${autoN} kicks fire ON THE VTS SERVER — they keep going even if you close the app · same recovery ladder (a save raises ONLY the next kick, a goal drops back to base) · pauses after a full losing ladder and will NOT restart by itself · ⚠ Stakes increase after each loss. Depth does not change the odds. A long losing streak can lose a large part or all of your balance. Real-money mode can lose your whole stake.`
            : `tap a side once → ${autoN} kicks fire back-to-back · a save auto-raises ONLY the next kick (recovery) · a goal drops straight back to base stake · pauses after a full losing ladder and will NOT restart by itself · ⚠ Stakes increase after each loss. Depth does not change the odds. A long losing streak can lose a large part or all of your balance. Real-money mode can lose your whole stake.`}</span>
        )}
        {autoProg && (
          <>
            <span data-testid="penalty-auto-progress" className="text-[10px] font-mono font-bold text-[#F7B731] animate-pulse">
              kick {autoProg.i}/{autoProg.n}{autoProg.rec ? ` · 🔁 recovery $${autoProg.rec.toFixed(2)}` : " · base stake"}…
            </span>
            <button data-testid="penalty-auto-stop" onClick={() => { autoStopRef.current = true; }}
              className="px-2.5 py-1 rounded-md bg-[#F23645] text-white text-[10px] font-black font-mono">
              ⏹ STOP
            </button>
          </>
        )}
      </div>
      {/* 📊 live season bar — kicks done + running profit */}
      {autoProg && <BurstProgress prog={{ done: autoProg.done ?? Math.max(0, autoProg.i - 1), n: autoProg.n, w: autoProg.w || 0, l: autoProg.l || 0, net: autoProg.net || 0 }} noun="kicks" id="penalty" />}

      {/* 🖥 live server-burst panel — polls while a ×50/×100 season runs on the server */}
      <ServerBurstPanel srv={srv} noun="kicks" />

      {/* 📡 market scanner — full details below, buttons stay next to the pitch */}
      <MarketScanner id="penalty" mode="evenodd" onChange={onScan}
        note="E/O = even vs odd share of the last 50 digits · kicks fire on the LOCKED market · history only, every kick stays ~50/50" />

      {/* scoreboard */}
      <div className="flex items-center justify-center gap-4 flex-wrap">
        <p data-testid="penalty-score" className="text-[11px] font-mono text-white/60">
          Goals <span className="text-[#089981] font-bold">{score.goals}</span> · Saved <span className="text-[#F23645] font-bold">{score.saves}</span>
        </p>
        <p data-testid="penalty-streak" className="text-[11px] font-mono text-white/60">
          Streak <span className="text-[#F7B731] font-bold">{streak}</span>/7 · Best <span className="text-white font-bold">{best}</span>
          <span className="text-white/35"> — 7 straight goals = 🏆 jackpot</span>
        </p>
        {badge7 && (
          <span data-testid="penalty-badge-7" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#F7B731]/50 bg-[#F7B731]/10 text-[9px] font-mono text-[#F7B731]">
            <Trophy className="w-3 h-3" /> SHARPSHOOTER
          </span>
        )}
        {(streak > 0 || best > 0) && (
          <button data-testid="penalty-share-btn"
            onClick={() => share("GOAL STREAK", Math.max(streak, best), "Penalty Shootout · beat the keeper")}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#2962FF]/50 bg-[#2962FF]/10 hover:bg-[#2962FF]/20 text-[9px] font-mono font-bold text-[#7aa2ff] transition-colors">
            <Share2 className="w-3 h-3" /> SHARE STREAK
          </button>
        )}
        {/* 🔇 mute — remembered across sessions */}
        <button data-testid="penalty-mute-btn" onClick={() => { const m = !muted; setMuted(m); setArcadeMuted(m); }}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-[9px] font-mono text-white/60 transition-colors">
          {muted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />} {muted ? "MUTED" : "SOUND ON"}
        </button>
      </div>
    </div>
  );
};

export default PenaltyShootout;
